/**
 * Récords y progreso por ejercicio contra la base (Req. 25).
 *
 * Nada se guarda: los récords salen de las series cada vez que se leen, así
 * que corregir o borrar una serie corrige el récord. Solo lee.
 */

import { db, leerSettings } from './schema';
import { leerHistorialDeEjercicio, type SesionDeEjercicio } from './entrenamientos';
import type { Exercise, TipoEjercicio } from './types';
import { modalidadDe } from '@/domain/modalidad';
import { esUnidadPeso, type UnidadPeso } from '@/domain/units';
import {
  METRICAS_DE,
  medidasDeSesion,
  metricasConDatos,
  recordsDeEjercicio,
  recordsNuevos,
  seriesQueCuentan,
  type Metrica,
  type RecordDeEjercicio,
  type RecordNuevo,
  type SesionDeRecords,
} from '@/domain/records';

/** El historial de un ejercicio en la forma que leen los récords. */
export function sesionesDeRecords(sesiones: readonly SesionDeEjercicio[]): SesionDeRecords[] {
  return sesiones.map(({ workout, series }) => ({
    workoutId: workout.id,
    fecha: workout.fecha,
    inicio: workout.inicio,
    modalidad: modalidadDe(workout),
    series,
  }));
}

/**
 * En qué unidad se leen los récords de un ejercicio: la que se usa para
 * capturar su carga (Req. 9.5), para que el récord se pueda comparar con lo
 * que dice su rutina y su historial. Si se captura en placas o no tiene una
 * propia, la preferida.
 */
export function unidadDeEjercicio(ejercicio: Pick<Exercise, 'unidadCaptura'>, preferida: UnidadPeso): UnidadPeso {
  return esUnidadPeso(ejercicio.unidadCaptura) ? ejercicio.unidadCaptura : preferida;
}

export interface ProgresoDeEjercicio {
  nombre: string;
  tipo: TipoEjercicio;
  /** La unidad en que se leen sus cargas. */
  unidad: UnidadPeso;
  /** Todas las sesiones terminadas con ese ejercicio, de la más reciente a la más antigua. */
  sesiones: SesionDeRecords[];
  records: RecordDeEjercicio[];
  /** Las métricas de su tipo que tienen datos, para la gráfica. */
  metricas: Metrica[];
  /** Las que su tipo lleva, aunque todavía no tengan datos. */
  metricasDelTipo: readonly Metrica[];
  /** Series hechas con la carga en placas: no entran en peso, 1RM ni volumen. */
  enPlacas: number;
}

/**
 * LECTURA PURA: los récords de un ejercicio y sus sesiones para la gráfica
 * (Req. 25.1, 25.3). Cuenta el gimnasio y la casa: el ejercicio es el mismo.
 */
export async function leerProgresoDeEjercicio(exerciseId: string): Promise<ProgresoDeEjercicio | null> {
  const [ejercicio, ajustes] = await Promise.all([db.exercises.get(exerciseId), leerSettings()]);
  if (ejercicio === undefined) return null;
  const sesiones = sesionesDeRecords(await leerHistorialDeEjercicio(exerciseId));
  return {
    nombre: ejercicio.nombre,
    tipo: ejercicio.tipo,
    unidad: unidadDeEjercicio(ejercicio, ajustes.unidadPeso),
    sesiones,
    records: recordsDeEjercicio(sesiones, ejercicio.tipo),
    metricas: metricasConDatos(sesiones, ejercicio.tipo),
    metricasDelTipo: METRICAS_DE[ejercicio.tipo],
    enPlacas: sesiones.reduce((n, s) => n + medidasDeSesion(s).enPlacas, 0),
  };
}

export interface EjercicioConRecords {
  exerciseId: string;
  nombre: string;
  /** La unidad con que se lee la carga de ese ejercicio. */
  unidad: UnidadPeso;
  records: RecordNuevo[];
}

export interface RecordsDelEntrenamiento {
  /** Un renglón por ejercicio con récords nuevos, en el orden del entrenamiento. */
  porEjercicio: EjercicioConRecords[];
  total: number;
  /** Los ejercicios que se estrenaron: no son récord, pero vale decirlo. */
  primeraVez: string[];
}

/**
 * LECTURA PURA: los récords que rompió un entrenamiento (Req. 25.4), contra
 * las sesiones anteriores de cada ejercicio. Un ejercicio que aparece dos
 * veces en el entrenamiento cuenta como una sola sesión: sus series se juntan.
 */
export async function leerRecordsDelEntrenamiento(workoutId: string): Promise<RecordsDelEntrenamiento> {
  const vacio: RecordsDelEntrenamiento = { porEjercicio: [], total: 0, primeraVez: [] };
  const workout = await db.workouts.get(workoutId);
  if (workout === undefined) return vacio;
  const [items, series, ajustes] = await Promise.all([
    db.workoutExercises.where('workoutId').equals(workoutId).toArray(),
    db.workoutSets.where('workoutId').equals(workoutId).toArray(),
    leerSettings(),
  ]);
  // En el orden del entrenamiento, sin repetir el ejercicio que va dos veces
  const ids = [...new Set(items.sort((a, b) => a.orden - b.orden).map((i) => i.exerciseId))];
  const ejercicios = await db.exercises.bulkGet(ids);
  const modalidad = modalidadDe(workout);

  const porEjercicio: EjercicioConRecords[] = [];
  const primeraVez: string[] = [];
  for (const [i, exerciseId] of ids.entries()) {
    const ejercicio = ejercicios[i];
    if (ejercicio === undefined) continue;
    const suyas = series.filter((s) => s.exerciseId === exerciseId);
    if (seriesQueCuentan(suyas).length === 0) continue;
    const sesion: SesionDeRecords = {
      workoutId,
      fecha: workout.fecha,
      inicio: workout.inicio,
      modalidad,
      series: suyas,
    };
    const historial = sesionesDeRecords(await leerHistorialDeEjercicio(exerciseId));
    const anteriores = historial.filter((s) => s.workoutId !== workoutId && s.inicio < workout.inicio);
    const records = recordsNuevos(sesion, anteriores, ejercicio.tipo);
    if (records.length > 0) {
      porEjercicio.push({
        exerciseId,
        nombre: ejercicio.nombre,
        unidad: unidadDeEjercicio(ejercicio, ajustes.unidadPeso),
        records,
      });
    }
    else if (anteriores.length === 0) primeraVez.push(ejercicio.nombre);
  }
  return {
    porEjercicio,
    total: porEjercicio.reduce((n, e) => n + e.records.length, 0),
    primeraVez,
  };
}
